import { forwardRef, useEffect, useRef } from 'react';

import { useWebViewPlugin } from './useWebViewPlugin';

export type GameRunnerProps = { title: string; src: string; clientID?: string; onReady?: () => void };

// eslint-disable-next-line
export const GameRunner = forwardRef<HTMLIFrameElement, GameRunnerProps>(({ title, src, clientID, onReady }, ref) => {
    const webviewRef = useRef(null);
    useWebViewPlugin(ref || webviewRef, src, clientID, onReady);

    useEffect(() => {
        const iframe: HTMLIFrameElement | null = (ref as any)?.current || (webviewRef as any)?.current;
        if (!iframe) {
            return () => {};
        }

        const measureViewportUnit = (unit: 'svh' | 'lvh' | 'dvh') => {
            const probe = document.createElement('div');
            probe.style.position = 'fixed';
            probe.style.height = `100${unit}`;
            probe.style.width = '1px';
            probe.style.pointerEvents = 'none';
            probe.style.visibility = 'hidden';
            document.body.appendChild(probe);
            const h = Math.round(probe.getBoundingClientRect().height);
            probe.remove();
            return h;
        };

        const measureViewportWidthUnit = (unit: 'svw' | 'lvw' | 'dvw') => {
            const probe = document.createElement('div');
            probe.style.position = 'fixed';
            probe.style.width = `100${unit}`;
            probe.style.height = '1px';
            probe.style.pointerEvents = 'none';
            probe.style.visibility = 'hidden';
            document.body.appendChild(probe);
            const w = Math.round(probe.getBoundingClientRect().width);
            probe.remove();
            return w;
        };

        const computeInsets = () => {
            const supports = (prop: string, value: string) => (window as any).CSS?.supports?.(prop, value) === true;
            const svhPx = supports('height', '100svh')
                ? measureViewportUnit('svh')
                : Math.round((window as any).visualViewport?.height ?? window.innerHeight);
            const lvhPx = supports('height', '100lvh') ? measureViewportUnit('lvh') : Math.round(window.outerHeight);

            const svwPx = supports('width', '100svw') ? measureViewportWidthUnit('svw') : Math.round(window.innerWidth);
            const lvwPx = supports('width', '100lvw')
                ? measureViewportWidthUnit('lvw')
                : Math.round(window.outerWidth ?? window.innerWidth);

            const deltaH = Math.max(0, lvhPx - svhPx);
            const deltaW = Math.max(0, lvwPx - svwPx);

            const epsilon = 4;
            let bottom = deltaW > deltaH + epsilon ? 0 : deltaH;

            const isLandscape =
                ((window.screen as any)?.orientation?.type?.startsWith?.('landscape') ?? false) ||
                window.innerWidth > window.innerHeight;
            if (isLandscape && bottom > 0) {
                bottom = Math.round(bottom / 5);
            }

            return { bottom };
        };

        const postInsets = () => {
            const insets = computeInsets();
            const svhPx = (window as any).CSS?.supports?.('height', '100svh')
                ? measureViewportUnit('svh')
                : Math.round((window as any).visualViewport?.height ?? window.innerHeight);
            try {
                iframe.contentWindow?.postMessage({ type: 'safe-area-insets', insets, svhPx }, '*');
            } catch (e) {
                // no-op
            }
        };

        const handleLoad = () => {
            postInsets();
        };
        iframe.addEventListener('load', handleLoad);
        postInsets();

        const vv = (window as any).visualViewport as VisualViewport | undefined;
        const onResize = () => {
            postInsets();
        };
        window.addEventListener('resize', onResize);
        vv?.addEventListener('resize', onResize);
        vv?.addEventListener('scroll', onResize);
        window.addEventListener('orientationchange', onResize);

        return () => {
            iframe.removeEventListener('load', handleLoad);
            window.removeEventListener('resize', onResize);
            vv?.removeEventListener('resize', onResize);
            vv?.removeEventListener('scroll', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, [ref, webviewRef]);

    return (
        <iframe
            style={{ border: 'none', height: '100%', width: '100%', margin: 0, padding: 0, overflow: 'hidden' }}
            ref={ref || webviewRef}
            title={title}
            src={src}
            data-client-id={clientID}
            // Délègue capteurs + autoplay aux jeux dans l'iframe : sans ça, les
            // jeux à visée gyroscopique (unshoot) n'ont ni orientation ni son,
            // et le blind test du tutoriel ne peut pas jouer de musique.
            // microphone : mode « À la voix » du Blind Test (dictée du téléphone) —
            // sans lui, un jeu chargé depuis un autre domaine n'a jamais le micro.
            allow="gyroscope; accelerometer; magnetometer; autoplay; microphone"
        />
    );
});
